<template>
  <div
    v-if="isComponentVisible"
    class="case-requests"
  >
    <naj-box :title="requestsFormLocals.requests">
      <div class="px-3 content-wrapper">
        <h3 class="primary--text font-weight-bold col col-2 mb-4">
          {{ requestsFormLocals.requests }}
        </h3>

        <naj-alert
          v-if="hasRequests"
          class="mb-4"
          type="information"
        >
          <template slot="content">
            <div class="request-alert-title">
              {{ requestsFormLocals.requestAlertTitle }}
            </div>
            {{ requestsFormLocals.requestAlertMessage }}
          </template>
        </naj-alert>

        <naj-data-table
          :headers="headers"
          :items="formModel.requests"
          :disable-items-per-page="true"
          :title="requestsFormLocals.requests"
          :error-message="errors.first(requestsFormLocals.requests)"
          :no-header="true"
        >
          <template slot-scope="{ item }">
            <v-btn
              v-if="!isViewMode"
              icon
              @click="showEditDataModal(item)"
            >
              <v-icon color="primary">
                mdi-lead-pencil
              </v-icon>
            </v-btn>

            <v-btn
              icon
              @click="selectViewItem(item)"
            >
              <v-icon color="primary">
                mdi-eye
              </v-icon>
            </v-btn>

            <v-btn
              v-if="!isViewMode"
              icon
              @click="removeItem(item)"
            >
              <v-icon color="red">
                mdi-delete
              </v-icon>
            </v-btn>
          </template>
        </naj-data-table>

        <v-row v-if="!isViewMode">
          <v-col
            cols="1"
            class="mb-6"
          >
            <naj-button
              icon="mdi-plus"
              @click="showAddDataModal"
            >
            </naj-button>
          </v-col>
        </v-row>
      </div>
    </naj-box>

    <naj-modal
      ref="addDataModal"
      :title="requestsFormLocals.requests"
      :close-button="true"
      show-footer-close-button
      :width="1200"
      class="validation-form-modal"
      @onClose="onCloseAddDataModal"
    >
      <v-form
        ref="requestFormRef"
        @submit.prevent="submitData"
      >
        <naj-input
          :type-id="formFieldType.selectbox"
          :label="requestsFormLocals.requestsList"
          :value.sync="requestForm.id"
          :value-name.sync="requestForm.name"
          :items="unUsedFilteredRequestsList"
          item-text="requestTypeName"
          item-value="requestTypeId"
          validates="required"
          item-description="formattedDescription"
          item-disabled="disabled"
          :disabled="isEditRequest"
          @change="requestsChanged"
        ></naj-input>
        <template v-if="loading">
          <naj-loader></naj-loader>
        </template>

        <template v-if="dynamicForm[selectedFormId]">
          <naj-dynamic-form
            :form-model="requestForm"
            :form-fields="dynamicForm[selectedFormId].fields"
            :root-form-model="formModel"
            :custom-component-name="dynamicForm[selectedFormId].customComponentName"
            :required-data="requiredData"
            :view-mode="isRequestFormInViewMode"
          ></naj-dynamic-form>
          <naj-input
            :type-id="formFieldType.textarea"
            :label="requestsFormLocals.requestTemplate"
            :value="requestForm.requestTemplate"
            :disabled="isRequestFormInViewMode"
            validates="max:5000"
            readonly
          ></naj-input>
        </template>
      </v-form>
      <template
        v-if="!isRequestFormInViewMode"
        slot="actions"
      >
        <naj-button
          :disabled="!requestForm.id || loading"
          @click="submitData"
        >
          {{ requestFormSubmitText }}
        </naj-button>
      </template>
    </naj-modal>
    <naj-confirmation
      :show-modal="showRemovePopup"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.common.removeConfirmationMessage"
      :ok-btn-label="local.common.remove"
      @ok="removeConfirmed"
      @cancel="showRemovePopup = false"
    ></naj-confirmation>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { executeString, uuidGenerator } from '@/helpers/utils';

const { mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');
const initRequestForm = () => ({
  id: null,
  name: null,
  formId: null,
  formName: null,
  repeatable: false,
  objectkey: uuidGenerator(),
});
export default {
  name: 'CaseRequests',
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    requiredData: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    isViewMode: {
      type: Boolean,
      required: false,
    },
    caseTypeId: {
      type: Number,
      required: true,
    },
  },
  inject: ['$validator'],
  data() {
    return {
      dynamicForm: {},
      requestForm: initRequestForm(),
      showRemovePopup: false,
      requestFormViewMode: false,
      isRequestFormAddMode: false,
      selectedItemToDelete: null,
      requestsList: [],
      loading: false,

    };
  },
  computed: {
    hasRequests() {
      return (this.formModel.requests || []).length > 0 || Boolean(this.requestForm.id);
    },
    requestsFormLocals() {
      return this.local.dynamicFormAdmin.requestsForm;
    },
    isComponentVisible() {
      return this.requestsList && this.requestsList.length;
    },
    selectedRequest() {
      return this.requestsList.find(item => item.requestTypeId == this.requestForm.id);
    },
    selectedRequestTypeId() {
      return this.selectedRequest && this.selectedRequest.requestTypeId;
    },
    selectedFormId() {
      return this.selectedRequest && this.selectedRequest.formId;
    },
    selectedRepeatable() {
      return this.selectedRequest && this.selectedRequest.repeatable;
    },
    headers() {
      return [
        {
          value: 'name',
          text: this.requestsFormLocals.requests,
        },
        {
          text: ' ',
          value: 'action',
        },
      ];
    },
    isRequestFormInViewMode() {
      return this.requestFormViewMode || this.isViewMode;
    },
    isEditRequest() {
      const requests = this.formModel.requests || [];
      return this.isRequestFormAddMode ? false : !!requests.find(request => request.id == this.requestForm.id);
    },
    requestFormSubmitText() {
      return this.isEditRequest
        ? this.local.common.update
        : this.local.common.add;
    },
    template() {
      return this.selectedRequest && this.selectedRequest.template;
    },
    unUsedFilteredRequestsList() {
      const filtered = this.isEditRequest
        ? this.requestsList.filter(
          r =>
            this.requestForm.id === r.requestTypeId ||
            !this.formModel.requests.some(fmr => fmr.id === r.requestTypeId)
        )
        : this.requestsList.filter(
          r =>
            (this.requestForm.id === r.requestTypeId && this.requestForm.repeatable) ||
            this.formModel.requests.some(
              fmr => fmr.id === r.requestTypeId && fmr.repeatable
            ) ||
            !this.formModel.requests.some(fmr => fmr.id === r.requestTypeId)
        );

      const mappedItems = filtered.map((item) => {
        const isSelected = (this.isEditRequest || this.isRequestFormInViewMode) ? this.formModel.requests.some(
          fmr => fmr.id === item.requestTypeId
        ) : false;

        return {
          ...item,
          formattedDescription: `${item.description}${item.tips ? '(' + item.tips + ')' : ''
            }`,
          disabled: item.expression && !executeString(item.expression, this.formModel),
          requestTypeName: `${item.requestTypeName}${!isSelected && item.expression && !executeString(item.expression, this.formModel)
            ? ' - لا يمكن اختيار الطلب لعدم انطباق الشروط'
            : ''
            }`,
        };
      });

      // sort items - enabled first, then disabled
      return mappedItems.sort((a, b) => {
        const aDisabled = a.expression && !executeString(a.expression, this.formModel);
        const bDisabled = b.expression && !executeString(b.expression, this.formModel);
        if (!aDisabled && bDisabled) return -1;
        if (aDisabled && !bDisabled) return 1;
        return 0;
      });
    },

  },
  // this.handlebars
  watch: {
    caseTypeId() {
      this.callGetCaseTypeRequestTypes();
    },
    requestForm: {
      handler() {
        this.$set(this.requestForm, 'requestTemplate', this.handlebars(this.template, this.requestForm));
        this.$set(this.requestForm, 'repeatable', this.selectedRepeatable);
      },
      deep: true,
    },
    'formModel.requests'() {
      this.$validator.flag(this.requestsFormLocals.requests, {
        untouched: false,
        touched: true,
        changed: true,
      });
      this.$validator.validate(this.requestsFormLocals.requests);
    },
  },
  mounted() {
    this.$set(this.formModel, 'requests', this.formModel.requests || []);
    this.callGetCaseTypeRequestTypes();
    this.$validator.attach({
      name: this.requestsFormLocals.requests,
      rules: 'required',
      getter: () => !this.isViewMode && this.formModel.requests,
    });
  },
  methods: {
    ...mapGlobalActions(['getDynamicForm', 'getCaseTypeRequestTypes']),
    requestsChanged() {
      if (this.selectedFormId)
        this.getForm(this.selectedFormId);
    },
    getForm(formId, trial = 0) {
      //!this.dynamicForm[formId]
      if (this.isRequestFormAddMode) {
        this.loading = true;
        this.getDynamicForm({
          formId,
          withoutParse: true,
        })
          .then((form) => {
            this.$set(this.dynamicForm, formId, form);
            this.$set(this.requestForm, 'formId', formId);
            this.$set(this.requestForm, 'formName', form.name);
            this.loading = false;
          })
          .catch(() => {
            if (trial < 2)
              this.getForm(formId, trial + 1);
            else
              this.closeAddDataModal();
          });
      }
    },
    removeItem(item) {
      this.showRemovePopup = true;
      this.selectedItemToDelete = item;
    },
    removeConfirmed() {
      const requestIndex = (this.formModel.requests || []).indexOf(this.selectedItemToDelete);
      this.$delete(this.formModel.requests, requestIndex);
      this.selectedItemToDelete = null;
      this.showRemovePopup = false;
    },
    closeAddDataModal() {
      this.$refs.addDataModal.close();
      this.onCloseAddDataModal();
    },
    onCloseAddDataModal() {
      this.selectedItemToDelete = null;
      this.requestForm = initRequestForm();
      this.requestFormViewMode = false;
      this.isRequestFormAddMode = false;
    },
    selectViewItem(item) {

      this.requestFormViewMode = true;
      this.isRequestFormAddMode = false;
      this.showEditDataModal(item);
    },
    showEditDataModal(item) {
      this.isRequestFormAddMode = false;
      this.requestForm = { ...item };
      if (this.selectedFormId)
        this.getForm(this.selectedFormId);
      this.showAddDataModal(this.isRequestFormAddMode);
    },
    showAddDataModal(isRequestFormAddMode) {
      this.isRequestFormAddMode = isRequestFormAddMode;
      this.$set(this.requestForm, 'template', this.requestForm.template || '');
      this.$refs.addDataModal.show();
    },
    submitData() {
      this.$refs.requestFormRef.$validator.validateAll()
        .then((isValid) => {
          if (isValid) {
            this.submitRequestForm();
            this.closeAddDataModal();
          }
        });
    },
    submitRequestForm() {
      this.formModel.requests = this.isEditRequest
        ? this.formModel.requests.map(item => item.objectkey == this.requestForm.objectkey ? this.requestForm : item)
        : [...this.formModel.requests, this.requestForm];
      this.$emit('change');
    },
    callGetCaseTypeRequestTypes() {
      this.loading = true;
      this.getCaseTypeRequestTypes(this.caseTypeId)
        .then(res => (this.requestsList = res))
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
<style lang="scss" scoped>
.case-requests {
  margin: 0 -12px;
}
.content-wrapper {
  border: 1px solid #e4e5e6 !important;
  margin-bottom: 25px;
}

.request-alert-title {
  color: #0065ff;
}
</style>
